import React from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import PrintLayout from '../PrintLayout';
import { BACKTICK_CODES } from '../../../utils/shortcodes';
import { computeGeniusTimeline } from '../../../utils/metrics';

const CODES = Object.keys(BACKTICK_CODES);

// Wonder is the highest elevation, Tenacity the lowest — CODES is already in
// that order, so elevation is just the reversed index (Wonder = 6 ... Tenacity = 1).
const ELEVATION_BY_CODE = {};
CODES.forEach((code, i) => { ELEVATION_BY_CODE[code] = CODES.length - i; });
const LABEL_BY_ELEVATION = {};
CODES.forEach(code => { LABEL_BY_ELEVATION[ELEVATION_BY_CODE[code]] = BACKTICK_CODES[code].label; });

function buildElevationTimeline(entries) {
  const timeline = computeGeniusTimeline(entries);
  const points = [];
  CODES.forEach(code => {
    (timeline[code] || []).forEach(minute => {
      points.push({ minute, elevation: ELEVATION_BY_CODE[code], code });
    });
  });
  return points.sort((a, b) => a.minute - b.minute);
}

function ElevationDot(props) {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;
  return <circle cx={cx} cy={cy} r={3} fill={BACKTICK_CODES[payload.code].color} stroke="#fff" strokeWidth={0.75} />;
}

const QUOTES = [
  "If you want to have effective meetings, you have to understand what kind of conversation you're having and ensure the right geniuses are active at that elevation.",
  'The single biggest problem in meetings is the lack of context. We try to wonder, invent, discern, galvanize, enable, and execute in the same 60 minutes, and it creates corporate whiplash.',
];

export default function PrintGeniusBarsAndEkg({ pageNumber, reportData, entries = [] }) {
  const totals = reportData.genius?.totals || {};
  const grandTotal = reportData.genius?.grandTotal || Object.values(totals).reduce((a, b) => a + b, 0);
  const maxTotal = Math.max(...CODES.map(c => totals[c] || 0), 1);
  const elevationData = buildElevationTimeline(entries);

  return (
    <PrintLayout pageNumber={pageNumber}>
      <div style={{ position: 'absolute', top: 0, bottom: 68, left: 0, right: 0, display: 'flex' }}>
        <div style={{ width: '32%', background: '#1e2e45', padding: '6% 5%', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '2px', color: '#5f7942', textTransform: 'uppercase', marginBottom: 10, fontFamily: 'Montserrat, sans-serif' }}>
            Section 3: Right Chemistry
          </div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 700, color: '#fff', lineHeight: 1.2, marginBottom: 14 }}>
            Meetings require the right genius at the right elevation.
          </div>
          <div style={{ width: 36, height: 2, background: '#5f7942', marginBottom: 14, borderRadius: 1 }} />
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, marginBottom: 14 }}>
            Even when people are behaving productively, they have to recognize that they are contributors in every conversation. Everything we say is either a contribution toward a better outcome for a topic, a distraction, or has no meaningful effect at all. The best way for a team to create outstanding outcomes is to create the right chemistry and utilize Patrick Lencioni's Working Genius model. The model helps teams discuss each topic at the appropriate elevation and encourages people to center their contributions around where that particular initiative sits within the six types of Working Genius.
          </div>
          {QUOTES.map((q, i) => (
            <div key={i} style={{ marginBottom: 12, padding: '10px 12px', borderLeft: '2px solid #5f7942', fontSize: 10, fontStyle: 'italic', color: 'rgba(255,255,255,0.55)', lineHeight: 1.65 }}>
              "{q}"
            </div>
          ))}
        </div>

        <div style={{ width: '68%', background: '#f5f4f0', padding: '4% 4.5%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 14 }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', color: '#6b757e', textTransform: 'uppercase', marginBottom: 10, fontFamily: 'Montserrat, sans-serif' }}>
              Right Genius Usage
            </div>
            <div style={{ background: 'white', border: '1px solid rgba(42,61,91,0.1)', borderRadius: 10, padding: '14px 18px', display: 'flex', gap: 10, alignItems: 'flex-end', height: 160 }}>
              {CODES.map(code => {
                const def = BACKTICK_CODES[code];
                const count = totals[code] || 0;
                const pct = grandTotal ? Math.round((count / grandTotal) * 100) : 0;
                // Reserve fixed pixel heights for the count/label/pct text (rather than
                // trusting the browser's default line-height) so the tallest bar's math
                // can't push the count label above the card's top edge. Budget is based on
                // the card's *content* box (global box-sizing: border-box means the card's
                // own padding/border eat into its declared 160px height).
                const COUNT_H = 12, LABEL_H = 11, PCT_H = 10, GAP = 4;
                const CARD_CONTENT_H = 160 - 14 * 2 - 1 * 2; // height minus vertical padding/border
                const BAR_MAX = CARD_CONTENT_H - COUNT_H - LABEL_H - PCT_H - GAP * 3;
                const barH = Math.max((count / maxTotal) * BAR_MAX, 3);
                return (
                  <div key={code} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: GAP }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#2a3d5b', height: COUNT_H, lineHeight: `${COUNT_H}px` }}>{count}</div>
                    <div style={{ width: '100%', maxWidth: 34, height: barH, background: def.color, borderRadius: 3 }} />
                    <div style={{ fontSize: 7.5, color: '#6b757e', textAlign: 'center', height: LABEL_H, lineHeight: `${LABEL_H}px`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{def.emoji} {def.label}</div>
                    <div style={{ fontSize: 7, color: '#9aa2ab', height: PCT_H, lineHeight: `${PCT_H}px` }}>{pct}%</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', color: '#6b757e', textTransform: 'uppercase', marginBottom: 10, fontFamily: 'Montserrat, sans-serif' }}>
              Chemistry Over Time
            </div>
            <div style={{ background: 'white', border: '1px solid rgba(42,61,91,0.1)', borderRadius: 10, padding: '10px 14px' }}>
              {elevationData.length > 1 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={elevationData} margin={{ top: 14, right: 8, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0ddd8" vertical={false} />
                    <XAxis dataKey="minute" type="number" tick={{ fontSize: 8 }} tickFormatter={(v) => `${v}m`} domain={['dataMin', 'dataMax']} />
                    <YAxis
                      dataKey="elevation"
                      type="number"
                      domain={[0.5, CODES.length + 0.5]}
                      ticks={CODES.map((_, i) => i + 1)}
                      tickFormatter={(v) => LABEL_BY_ELEVATION[v]}
                      tick={{ fontSize: 7.5 }}
                      width={66}
                    />
                    <Tooltip labelFormatter={(v) => `${v}m`} formatter={(v, _n, { payload }) => [BACKTICK_CODES[payload.code].label, 'Genius']} />
                    <Line type="linear" dataKey="elevation" stroke="#1e2e45" strokeWidth={1.25} dot={<ElevationDot />} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ fontSize: 10, color: '#6b757e', fontStyle: 'italic', padding: 20 }}>Not enough data to display chart.</div>
              )}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', fontSize: 8, marginTop: 4 }}>
                {CODES.map(code => (
                  <span key={code} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: BACKTICK_CODES[code].color }} />
                    {BACKTICK_CODES[code].label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PrintLayout>
  );
}
